import React, { useState, useRef, useEffect, useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import styles from "../styles/components/reclamation.module.scss";
import { backendLink, dataLink } from "../config";
import products from "../products.json";
import axios from "axios";
import xss from "xss";
import { se } from "date-fns/locale";

// This file contains all Drop down used in this app

// Normalize a string for searching: case-insensitive and accent-insensitive, so
// typing "bejaia" matches "Béjaïa" and "ain" matches "Aïn". Also strips Arabic
// diacritics so a search works whether or not the user types the harakat.
const normalizeSearch = (value) =>
  (value ?? "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // latin accents
    .replace(/[ً-ْ]/g, "") // arabic harakat
    .trim();

// Drop down for product list in consumer form used in old version now productPopup is used
export const DropDown = ({
  placeholder,
  setProduct,
  isVisible,
  setProductVisibility,
  setProductInputsVisibility,
}) => {
  const [isListDisplayed, setListVisibility] = useState(false);
  const modalContentRef = useRef(null);
  const { t, i18n } = useTranslation();

  const toggleList = () => {
    console.log("list toggled", isListDisplayed);
    setListVisibility(!isListDisplayed);
  };

  const handleClickOutside = (event) => {
    if (
      modalContentRef.current &&
      !modalContentRef.current.contains(event.target)
    ) {
      setListVisibility(false);
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible]);

  const onItemSelected = (item) => {
    toggleList();
    if (setProductInputsVisibility !== null) {
      setProductInputsVisibility(false);
      setProduct(item.text);
      setProductVisibility(true);
    }
  };

  return (
    <div
      ref={modalContentRef}
      className={classNames(
        styles.wilayaContainer,
        styles.productDropdownContainer,
        styles.width
      )}
      style={{ display: isVisible ? "block" : "none" }}
    >
      <div className={styles.dropdownContainerr}>
        <div className={styles.customDropdownn}>
          <input
            type="text"
            id="productDropdown"
            className={classNames(
              i18n.language === "fr"
                ? styles.customDropdownInput
                : styles.customDropdownArabicInput,
              styles.pointedArea
            )}
            name="product"
            placeholder={placeholder}
            onClick={toggleList}
            readOnly
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
          />
          <div
            className={classNames(styles.productList, {
              [styles.hidden]: !isListDisplayed,
            })}
            id="productList"
          >
            {products.map((product) => (
              <div
                key={product.id}
                className={styles.productListItem}
                onClick={() => onItemSelected(product)}
              >
                {product.text}
              </div>
            ))}
          </div>
          <div
            className={i18n.language === "ar" ? styles.arrowLeft : styles.arrow}
            id="productArrow"
            onClick={toggleList}
          >
            &#9660;
          </div>
        </div>
      </div>
    </div>
  );
};

// Drop down to select Wilaya used in consumer form and signup merchant form
export const DropDownWilaya = ({
  setWilaya,
  setCommuneList,
  wilaya,
  setCommune,
  onScroll,
}) => {
  const { t, i18n } = useTranslation();
  const [isListDisplayed, setListVisibility] = useState(false);
  const modalContentRef = useRef(null);
  const [wilayas, setWilayas] = useState([]);
  const [wilayaItem, setWilayaItem] = useState("");
  const [search, setSearch] = useState("");
  const searchInputRef = useRef(null);

  const toggleList = () => {
    onScroll();
    setSearch(""); // always reopen on a clean list
    setListVisibility(!isListDisplayed);
  };

  // Match on the french name, the arabic name, or the wilaya number
  const filteredWilayas = useMemo(() => {
    const query = normalizeSearch(search);
    if (!query) return wilayas;
    return wilayas.filter(
      (item) =>
        normalizeSearch(item.text).includes(query) ||
        normalizeSearch(item.text_ar).includes(query) ||
        String(item.id).startsWith(query)
    );
  }, [wilayas, search]);

  // Put the caret straight in the search box when the list opens
  useEffect(() => {
    if (isListDisplayed) searchInputRef.current?.focus();
  }, [isListDisplayed]);

  // const onItemSelected = (item) => {
  //   toggleList();
  //   const matches = communes.filter(commune => commune.wilaya_id === item.id);
  //   setCommune('');
  //   setCommuneList(matches);
  //   let wilayasel = i18n.language === 'ar' ? item.text_ar : item.text
  //   setWilaya(wilayasel);
  // }

  const onItemSelected = async (item) => {
    let wilayasel = item.text;
    setWilayaItem(item);
    setWilaya(wilayasel);

    toggleList();
    setCommuneList([]);
    setCommune("");

    // Load communes of selected Wilaya from json file
    try {
      // Fetch communes.json from public/data/
      const response = await fetch(`${dataLink}/data/communes.json`);

      if (!response.ok) {
        throw new Error(`Error fetching communes data: ${response.statusText}`);
      }

      // Convert response to JSON
      const data = await response.json();

      // Filter communes by wilaya_id
      const matches = data.filter((commune) => commune.wilaya_id === item.id);

      // Update state with filtered communes
      setCommuneList(matches);
    } catch (error) {
      console.error("Error fetching communes.json:", error);
    }
  };

  const handleClickOutside = (event) => {
    if (
      modalContentRef.current &&
      !modalContentRef.current.contains(event.target)
    ) {
      setListVisibility(false);
    }
  };

  // Load Wilayas from json file
  useEffect(() => {
    const fetchWilayas = async () => {
      try {
        const response = await fetch(`${dataLink}/data/wilayas.json`);
        // const response = await fetch(`${process.env.PUBLIC_URL}/data/wilayas.json`);
        if (!response.ok) {
          throw new Error(
            `Error fetching wilayas data: ${response.statusText}`
          );
        }
        const data = await response.json();
        setWilayas(data);
      } catch (error) {
        console.error("Error fetching wilayas.json:", error);
      }
    };

    fetchWilayas();
  }, []);

  useEffect(() => {
    if (isListDisplayed) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isListDisplayed]);

  //Switch company
  const [selectedCompany, setSelectedCompany] = useState(null);
  const companyName = localStorage.getItem("selectedCompany");

  return (
    <div
      ref={modalContentRef}
      className={classNames(
        styles.wilayaContainer,
        styles.productDropdownContainer,
        styles.width
      )}
    >
      <div className={styles.dropdownContainerr}>
        <div className={styles.customDropdownn}>
          {/* show name of wilaya in arabic if arabic language selected else in french */}
          <input
            type="text"
            id="productDropdown"
            className={classNames("dropdown-input", styles.pointedArea)}
            name="product"
            placeholder={t("chooseWilaya")}
            onClick={toggleList}
            readOnly
            value={
              wilayaItem !== ""
                ? i18n.language === "fr" || i18n.language === "en"
                  ? `${wilayaItem.id} - ${wilayaItem.text} (${wilayaItem.text_ar})`
                  : `${wilayaItem.id} - ${wilayaItem.text_ar}`
                : ""
            }
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
          />
          <div
            className={classNames(styles.productList, {
              [styles.hidden]: !isListDisplayed,
            })}
            id="productList"
            style={{ maxHeight: "328px" }}
          >
            <input
              ref={searchInputRef}
              type="text"
              className={styles.dropdownSearchInput}
              placeholder={t("searchWilaya")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              dir={i18n.language === "ar" ? "rtl" : "ltr"}
            />
            {filteredWilayas.length === 0 ? (
              <div className={styles.dropdownNoResult}>{t("noResults")}</div>
            ) : (
              filteredWilayas.map((wilaya) => (
                <div
                  key={wilaya.id}
                  className={
                    i18n.language === "ar"
                      ? styles.productListItemArabic
                      : styles.productListItem
                  }
                  onClick={() => onItemSelected(wilaya)}
                >
                  {i18n.language === "fr" || i18n.language === "en"
                    ? wilaya.id + " - " + wilaya.text + ` (${wilaya.text_ar})`
                    : wilaya.id + "- " + wilaya.text_ar}
                </div>
              ))
            )}
          </div>
          <div
            className={i18n.language === "ar" ? styles.arrowLeft : styles.arrow}
            id="productArrow"
            onClick={toggleList}
          >
            &#9660;
          </div>
        </div>
      </div>
    </div>
  );
};

export const WilayaDropDown =({setWilaya , value}) => {

  const { t, i18n } = useTranslation();
  const [isListDisplayed, setListVisibility] = useState(false);
  const modalContentRef = useRef(null);
  const [wilayas, setWilayas] = useState([]);
  const [wilayaItem, setWilayaItem] = useState(null);

  const toggleList = () => {
    setListVisibility(!isListDisplayed);
  };

  const onItemSelected = async (item) => {
    let wilayasel = item.text;
    setWilayaItem(item);
    setWilaya(wilayasel);
    toggleList();
  }

  const handleClickOutside = (event) => {
    if (
      modalContentRef.current &&
      !modalContentRef.current.contains(event.target)
    ) {
      setListVisibility(false);
    }
  };

  useEffect(() => {
    const fetchWilayas = async () => {
      try {
        const response = await fetch(`${dataLink}/data/wilayas.json`);
        // const response = await fetch(`${process.env.PUBLIC_URL}/data/wilayas.json`);
        if (!response.ok) {
          throw new Error(
            `Error fetching wilayas data: ${response.statusText}`
          );
        }
        const data = await response.json();
        setWilayas(data);
      } catch (error) {
        console.error("Error fetching wilayas.json:", error);
      }
    };

    fetchWilayas();
  }, []);
    useEffect(() => {
    if (isListDisplayed) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isListDisplayed]);

  useEffect(() => {
    if (!value || wilayas.length === 0) return;

    const foundWilaya = wilayas.find(
      (w) => w.text === value || w.text_ar === value
    );

    if (foundWilaya) {
      setWilayaItem(foundWilaya);
    }
  }, [value, wilayas]);

  return (
    <div
      ref={modalContentRef}
      className={classNames(
        styles.wilayaContainer,
        styles.productDropdownContainer,
        styles.width
      )}
    >
      <div className={styles.dropdownContainerr}>
        <div className={styles.customDropdownn}>
          {/* show name of wilaya in arabic if arabic language selected else in french */}
          <input
            type="text"
            id="productDropdown"
            className={classNames("dropdown-input", styles.pointedArea)}
            name="product"
            placeholder={t("WillayaDeResidence")}
            onClick={toggleList}
            readOnly
            value={
              wilayaItem 
                ? i18n.language === "fr" || i18n.language === "en"
                  ? `${wilayaItem.id} - ${wilayaItem.text} (${wilayaItem.text_ar})`
                  : `${wilayaItem.id} - ${wilayaItem.text_ar}`
                : ""
            }
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
          />
          <div
            className={classNames(styles.productList, {
              [styles.hidden]: !isListDisplayed,
            })}
            id="productList"
            style={{ maxHeight: "328px" }}
          >
            {wilayas.map((wilaya) => (
              <div
                key={wilaya.id}
                className={
                  i18n.language === "ar"
                    ? styles.productListItemArabic
                    : styles.productListItem
                }
                onClick={() => onItemSelected(wilaya)}
              >
                {i18n.language === "fr" || i18n.language === "en"
                  ? wilaya.id + " - " + wilaya.text + ` (${wilaya.text_ar})`
                  : wilaya.id + "- " + wilaya.text_ar}
              </div>
            ))}
          </div>
          <div
            className={i18n.language === "ar" ? styles.arrowLeft : styles.arrow}
            id="productArrow"
            onClick={toggleList}
          >
            &#9660;
          </div>
        </div>
      </div>
    </div>
  );


}
// Drop down to select Commune used in consumer form and signup merchant form
export const DropDownCommune = ({ communes, setCommune, commune }) => {
  const { t, i18n } = useTranslation();
  const [isListDisplayed, setListVisibility] = useState(false);
  const modalContentRef = useRef(null);
  const [communeItem, setCommuneItem] = useState("");
  const [search, setSearch] = useState("");
  const searchInputRef = useRef(null);

  // A commune can only be picked once a wilaya has been chosen: the parent only
  // fills `communes` after a wilaya is selected.
  const isWilayaSelected = Array.isArray(communes) && communes.length > 0;

  useEffect(() => {
    if (commune === "") {
      setCommuneItem("");
    }
  }, [commune]);

  // Changing wilaya swaps the commune list, so drop any stale search text
  useEffect(() => {
    setSearch("");
  }, [communes]);

  const toggleList = () => {
    setSearch(""); // always reopen on a clean list
    setListVisibility(!isListDisplayed);
  };

  // Put the caret straight in the search box when the list opens
  useEffect(() => {
    if (isListDisplayed && isWilayaSelected) searchInputRef.current?.focus();
  }, [isListDisplayed, isWilayaSelected]);

  const onItemSelected = (item) => {
    toggleList();
    setCommuneItem(item);
    // let selCommune = i18n.language === 'fr' ? item.name : item.ar_name
    let selCommune = item.name;
    setCommune(selCommune);
  };

  const handleClickOutside = (event) => {
    if (
      modalContentRef.current &&
      !modalContentRef.current.contains(event.target)
    ) {
      setListVisibility(false);
    }
  };

  useEffect(() => {
    if (isListDisplayed) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isListDisplayed]);

  // Communes of the selected wilaya, filtered by the search then sorted alphabetically.
  // Match on the french name, the arabic name, or the postal code.
  const sortedCommunes = useMemo(() => {
    if (!isWilayaSelected) return [];
    const query = normalizeSearch(search);
    const matches = query
      ? communes.filter(
          (item) =>
            normalizeSearch(item.name).includes(query) ||
            normalizeSearch(item.ar_name).includes(query) ||
            String(item.post_code ?? "").startsWith(query)
        )
      : communes;
    return [...matches].sort((a, b) => {
      if (i18n.language === "ar") {
        return a.ar_name.localeCompare(b.ar_name, "ar");
      } else {
        return a.name.localeCompare(b.name, i18n.language);
      }
    });
  }, [communes, isWilayaSelected, search, i18n.language]);

  return (
    <div
      ref={modalContentRef}
      className={classNames(
        styles.wilayaContainer,
        styles.productDropdownContainer,
        styles.width
      )}
    >
      <div className={styles.dropdownContainerr}>
        <div className={styles.customDropdownn}>
          <input
            type="text"
            id="productDropdown"
            className={classNames("dropdown-input", styles.pointedArea)}
            name="product"
            placeholder={
              isWilayaSelected ? t("chooseCommune") : t("selectWilayaFirst")
            }
            onClick={toggleList}
            readOnly
            // value={commune}
            value={
              communeItem !== ""
                ? i18n.language === "fr" || i18n.language === "en"
                  ? `${communeItem.name}`
                  : `${communeItem.ar_name}`
                : ""
            }
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
          />
          <div
            className={classNames(styles.productList, {
              [styles.hidden]: !isListDisplayed,
            })}
            id="productList"
            style={{ maxHeight: "267px" }}
          >
            {!isWilayaSelected ? (
              // No wilaya picked yet, so there is nothing to search through
              <div className={styles.dropdownNoResult}>
                {t("selectWilayaFirst")}
              </div>
            ) : (
              <>
                <input
                  ref={searchInputRef}
                  type="text"
                  className={styles.dropdownSearchInput}
                  placeholder={t("searchCommune")}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  dir={i18n.language === "ar" ? "rtl" : "ltr"}
                />
                {sortedCommunes.length === 0 ? (
                  <div className={styles.dropdownNoResult}>{t("noResults")}</div>
                ) : (
                  sortedCommunes.map((commune) => (
                    <div
                      key={commune.id}
                      className={
                        i18n.language === "ar"
                          ? styles.communeListItemArabic
                          : styles.communeListItem
                      }
                      onClick={() => onItemSelected(commune)}
                    >
                      {i18n.language === "ar" ? commune.ar_name : commune.name}
                    </div>
                  ))
                )}
              </>
            )}
          </div>
          <div
            className={i18n.language === "ar" ? styles.arrowLeft : styles.arrow}
            id="productArrow"
            onClick={toggleList}
          >
            &#9660;
          </div>
        </div>
      </div>
    </div>
  );
};

// Component assembling DropDownWilaya + DropDownCommune
export const DropDownLocation = ({
  setWilaya,
  setCommune,
  setmarket_name,
  wilaya,
  commune,
  market_name,
  onScroll,
  showMarketName = true,
}) => {
  const [communesList, setCommunesList] = useState(null);
  const { t, i18n } = useTranslation();
  return (
    <div>
      <DropDownWilaya
        setCommuneList={setCommunesList}
        setWilaya={setWilaya}
        wilaya={wilaya}
        setCommune={setCommune}
        onScroll={onScroll}
      />
      <DropDownCommune
        communes={communesList}
        setCommune={setCommune}
        commune={commune}
      />
      {showMarketName && (
        <input
          type="text"
          placeholder={t("market_namePlaceholder")}
          onInput={(event) => {
            if (event.target.value.length > 25) {
              event.target.value = event.target.value.substring(0, 25);
          }
        }}
        maxLength="25"
        onChange={(e) => {
          const sanitizedValue = xss(e.target.value);
          // Only keep alphabetic characters
          const alphabeticValue = sanitizedValue.replace(
            /[^A-Za-z0-9\u0600-\u06FF\s]/g,
            ""
          );
          setmarket_name(alphabeticValue);
        }}
        value={market_name}
        dir={i18n.language === "ar" ? "rtl" : "ltr"}
        lang={i18n.language === "ar" ? "ar" : "fr"} // Set the language for keyboard input
        inputMode={i18n.language === "ar" ? "text" : "latin"} // Suggest Arabic or Latin keyboard
        className={styles.nameInput}
      />)}
    </div>
  );
};

// Drop down for prodcuts list in merchant form used in old version
export const DropDownCommercant = ({
  placeholder,
  product,
  setProduct,
  isVisible,
  setProductVisibility,
  setProductInputsVisibility,
  productVisibility,
  tous,
  setTous,
}) => {
  const [isListDisplayed, setListVisibility] = useState(false);
  const [allSelected, setAllSelected] = useState(false); // Track if all products are selected
  const [products, setProducts] = useState([]);
  const modalContentRef = useRef(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    axios
      .get(`${backendLink}shared/getAllProduct`)
      .then((response) => {
        setProducts(response.data.data.products);
      })
      .catch((error) => {
        console.error("Error fetching alerts:", error);
      });
  }, []);

  const toggleList = () => {
    setListVisibility(!isListDisplayed);
  };

  const handleClickOutside = (event) => {
    if (
      modalContentRef.current &&
      !modalContentRef.current.contains(event.target)
    ) {
      setListVisibility(false);
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible]);

  const onItemSelected = (item) => {
    toggleList();
    setTous(false);

    if (setProductInputsVisibility !== null) {
      const currentProducts = product; // Get the current value of the product array

      // Check if there are less than 3 elements in the product array before adding the new one
      if (currentProducts.length < 3) {
        setProduct([...currentProducts, item]); // Add the selected product to the array
        setProductVisibility(true);

        // Ensure productVisibility is an array before updating it
        const updatedVisibility = Array.isArray(productVisibility)
          ? [...productVisibility, true]
          : [true];
        setProductVisibility(updatedVisibility);
        console.log([...currentProducts, item], updatedVisibility);

        if (currentProducts.length === 2) setProductInputsVisibility(false);
      } else {
        console.log("Product list already contains 3 elements.");
      }
    }
  };

  // Select or deselect all products
  const selectAllItems = () => {
    toggleList();
    setProduct([
      {
        id: 0,
        name: t("allProducts"),
        // name: i18n.language === 'ar' ? "جميع المنتجات" : "Tous les produits"
      },
    ]);
    setProductVisibility([true]); // Set visibility for all selected products
    setAllSelected(true); // Mark all items as selected
    setTous(true);
    setProductInputsVisibility(false);
  };

  // Filter out the selected products from the list
  const filteredProducts = products.filter(
    (productItem) => !product.some((p) => p.id === productItem.id)
  );

  return (
    <div
      ref={modalContentRef}
      className={classNames(
        styles.wilayaContainer,
        styles.productDropdownContainer,
        styles.width
      )}
      style={{ display: isVisible ? "block" : "none" }}
    >
      <div className={styles.dropdownContainerr}>
        <div className={styles.customDropdownCommercant}>
          <input
            type="text"
            id="productDropdown"
            className={classNames(
              i18n.language === "fr"
                ? styles.customDropdownInput
                : styles.customDropdownArabicInput,
              styles.pointedArea
            )}
            name="product"
            // placeholder={allSelected ? 'Tous les produits' : placeholder} // Show 'Tous les produits' when all products are selected
            placeholder={placeholder}
            onClick={toggleList}
            readOnly
            // value={allSelected ? 'Tous les produits' : ''}
            value=""
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
          />
          <div
            className={classNames(styles.productList, {
              [styles.hidden]: !isListDisplayed,
            })}
            id="productList"
          >
            {/* <div className={styles.productListItem} onClick={selectAllItems}>
              {allSelected ? 'Désélectionner tout' : 'Sélectionner tout'}
            </div> */}
            <div
              className={styles.productListItem}
              onClick={selectAllItems}
              dir={i18n.language === "ar" ? "rtl" : "ltr"}
            >
              {/* {'Sélectionner tout'} */}
              {t("allProducts")}
            </div>
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className={styles.productListItem}
                onClick={() => onItemSelected(product)}
              >
                {product.name}
              </div>
            ))}
          </div>
          <div
            className={i18n.language === "ar" ? styles.arrowLeft : styles.arrow}
            id="productArrow"
            onClick={toggleList}
          >
            &#9660;
          </div>
        </div>
      </div>
    </div>
  );
};

const sources = [
  { name: "Grossiste" },
  { name: "Distributeur" },
  { name: "Autre" },
];

export const DropDownSource = ({ setSource, source, isAuthenticated }) => {
  const { t, i18n } = useTranslation();
  const [isListDisplayed, setListVisibility] = useState(false);
  const modalContentRef = useRef(null);
  const dropdownListRef = useRef(null);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState("auto"); // Store max height of dropdown
  const [sources, setSources] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios
      .get(`${backendLink}merchant/fromWhere`, {
        withCredentials: true,
      })
      .then((response) => {
        setSources(response.data.data.values);
      })
      .catch((error) => {
        console.error("Error fetching alerts:", error);
      });
  }, [isAuthenticated]);

  const toggleList = () => {
    setListVisibility(!isListDisplayed);
  };

  const onItemSelected = (item) => {
    toggleList();
    setSource(item);
  };

  const handleClickOutside = (event) => {
    if (
      modalContentRef.current &&
      !modalContentRef.current.contains(event.target)
    ) {
      setListVisibility(false);
    }
  };

  useEffect(() => {
    if (isListDisplayed && modalContentRef.current) {
      const rect = modalContentRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const availableSpaceBelow = windowHeight - rect.bottom;
      const dropdownHeight = dropdownListRef.current?.scrollHeight || 0;

      // Set the max height of the dropdown based on the available space below the input
      if (dropdownHeight > availableSpaceBelow) {
        setDropdownMaxHeight(`${availableSpaceBelow - 30}px`); // Leave some space for padding/margin
      } else {
        setDropdownMaxHeight("auto"); // If there's enough space, no need to limit height
      }
    }
  }, [isListDisplayed]);

  useEffect(() => {
    if (isListDisplayed) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isListDisplayed]);

  return (
    <div
      ref={modalContentRef}
      className={classNames(
        styles.wilayaContainer,
        styles.productDropdownContainer,
        styles.width
      )}
    >
      <div className={styles.dropdownContainerr}>
        <div className={styles.customDropdownnSource}>
          <input
            type="text"
            id="productDropdown"
            className={classNames("dropdown-input", styles.pointedArea)}
            name="product"
            placeholder={t("chooseSource")}
            onClick={toggleList}
            readOnly
            value={
              i18n.language === "ar"
                ? source.arabic_translation
                : i18n.language === "en"
                ? source.english_translation
                : source.name
            }
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
          />
          {sources !== null && (
            <div
              ref={dropdownListRef}
              className={classNames(styles.productList, {
                [styles.hidden]: !isListDisplayed,
              })}
              id="productList"
              style={{
                position: "absolute",
                zIndex: 999,
                maxHeight: dropdownMaxHeight, // Dynamically set max height
                overflowY: "auto", // Enable scrolling if content exceeds height
                top: "100%", // Ensure dropdown stays below the toggler
                left: 0,
                width: "100%",
                backgroundColor: "white",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              {sources.map((commune) => (
                <div
                  key={commune.name}
                  className={
                    i18n.language === "ar"
                      ? styles.communeListItemArabic
                      : styles.communeListItem
                  }
                  onClick={() => onItemSelected(commune)}
                >
                  {/* {t(commune.name)} */}
                  {i18n.language === "ar"
                    ? commune.arabic_translation
                    : i18n.language === "en"
                    ? commune.english_translation
                    : commune.name}
                </div>
              ))}
            </div>
          )}
          <div
            className={i18n.language === "ar" ? styles.arrowLeft : styles.arrow}
            id="productArrow"
            onClick={toggleList}
          >
            &#9660;
          </div>
        </div>
      </div>
    </div>
  );
};

export const DropDownType = ({ setType, type }) => {
  const { t, i18n } = useTranslation();
  const [isListDisplayed, setListVisibility] = useState(false);
  const modalContentRef = useRef(null);
  // const types = useSelector((state) => state.tenant.tenant.merchantTypes);
  const [types, setTypes] = useState([]);

  useEffect(() => {
    axios
      .get(`${backendLink}merchant/getMerchantTypes`)
      .then((response) => {
        // Ensure response.data and response.data.data exist before accessing tips
        const tips = response.data?.data?.merchant_types || [];
        setTypes(tips);
      })
      .catch((error) => {
        console.error("Error fetching tips:", error);
      });
  }, []);

  const toggleList = () => {
    setListVisibility(!isListDisplayed);
  };

  const onItemSelected = (item) => {
    toggleList();
    setType(item);
  };

  const handleClickOutside = (event) => {
    if (
      modalContentRef.current &&
      !modalContentRef.current.contains(event.target)
    ) {
      setListVisibility(false);
    }
  };

  useEffect(() => {
    if (isListDisplayed) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isListDisplayed]);

  return (
    <div
      ref={modalContentRef}
      className={classNames(
        styles.wilayaContainer,
        styles.productDropdownContainer,
        styles.width
      )}
    >
      <div className={styles.dropdownContainerr}>
        <div className={styles.customDropdownn}>
          <input
            type="text"
            id="productDropdown"
            className={classNames("dropdown-input", styles.pointedArea)}
            name="product"
            placeholder={t("chooseType")}
            onClick={toggleList}
            readOnly
            value={
              i18n.language === "ar"
                ? type.arabic_translation
                : i18n.language === "en"
                ? type.english_translation
                : type.name
            }
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
          />
          {sources !== null && (
            <div
              className={classNames(styles.productList, {
                [styles.hidden]: !isListDisplayed,
              })}
              id="productList"
            >
              {types.map((commune) => (
                <div
                  key={commune.name}
                  className={
                    i18n.language === "ar"
                      ? styles.communeListItemArabic
                      : styles.communeListItem
                  }
                  onClick={() => onItemSelected(commune)}
                >
                  {/* {t(commune.name)} */}
                  {i18n.language === "ar"
                    ? commune.arabic_translation
                    : i18n.language === "en"
                    ? commune.english_translation
                    : commune.name}
                </div>
              ))}
            </div>
          )}
          <div
            className={i18n.language === "ar" ? styles.arrowLeft : styles.arrow}
            id="productArrow"
            onClick={toggleList}
          >
            &#9660;
          </div>
        </div>
      </div>
    </div>
  );
};

export const DropDownDelays = ({ setDelay, delay, isAuthenticated }) => {
  const { t, i18n } = useTranslation();
  const [isListDisplayed, setListVisibility] = useState(false);
  const modalContentRef = useRef(null);
  const [delays, setDelays] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios
      .get(`${backendLink}merchant/fromWhen`, {
        withCredentials: true,
      })
      .then((response) => {
        setDelays(response.data.data.values);
      })
      .catch((error) => {
        console.error("Error fetching alerts:", error);
      });
  }, [isAuthenticated]);

  const toggleList = () => {
    setListVisibility(!isListDisplayed);
  };

  const onItemSelected = (item) => {
    toggleList();
    setDelay(item);
  };

  const handleClickOutside = (event) => {
    if (
      modalContentRef.current &&
      !modalContentRef.current.contains(event.target)
    ) {
      setListVisibility(false);
    }
  };

  useEffect(() => {
    if (isListDisplayed) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isListDisplayed]);

  return (
    <div
      ref={modalContentRef}
      className={classNames(
        styles.wilayaContainer,
        styles.productDropdownContainer,
        styles.width
      )}
    >
      <div className={styles.dropdownContainerr}>
        <div className={styles.customDropdownn}>
          <input
            type="text"
            id="productDropdown"
            className={classNames("dropdown-input", styles.pointedArea)}
            name="product"
            placeholder={t("chooseDelay")}
            onClick={toggleList}
            readOnly
            value={
              i18n.language === "ar"
                ? delay.arabic_translation
                : i18n.language === "en"
                ? delay.english_translation
                : delay.name
            }
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
          />
          {delays !== null && (
            <div
              className={classNames(styles.productListDelay, {
                [styles.hidden]: !isListDisplayed,
              })}
              id="productList"
            >
              {delays.map((commune) => (
                <div
                  key={commune.name}
                  className={
                    i18n.language === "ar"
                      ? styles.communeListItemArabic
                      : styles.communeListItem
                  }
                  onClick={() => onItemSelected(commune)}
                >
                  {/* {t(commune.name)} */}
                  {i18n.language === "ar"
                    ? commune.arabic_translation
                    : i18n.language === "en"
                    ? commune.english_translation
                    : commune.name}
                </div>
              ))}
            </div>
          )}
          <div
            className={i18n.language === "ar" ? styles.arrowLeft : styles.arrow}
            id="productArrow"
            onClick={toggleList}
          >
            &#9660;
          </div>
        </div>
      </div>
    </div>
  );
};

export const OtherRequestType = ({ setType, type }) => {
  const { t, i18n } = useTranslation();
  const [isListDisplayed, setListVisibility] = useState(false);
  const modalContentRef = useRef(null);
  // const types = useSelector((state) => state.tenant.tenant.merchantTypes);
  const [types, setTypes] = useState([]);

  useEffect(() => {
    axios
      .get(`${backendLink}otherRequests/getOtherRequestTypes`)
      .then((response) => {
        // Ensure response.data and response.data.data exist before accessing tips
        const tips = response.data?.data || [];
        setTypes(tips);
      })
      .catch((error) => {
        console.error("Error fetching tips:", error);
      });
  }, []);

  const toggleList = () => {
    setListVisibility(!isListDisplayed);
  };

  const onItemSelected = (item) => {
    toggleList();
    setType(item);
  };

  const handleClickOutside = (event) => {
    if (
      modalContentRef.current &&
      !modalContentRef.current.contains(event.target)
    ) {
      setListVisibility(false);
    }
  };

  useEffect(() => {
    if (isListDisplayed) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isListDisplayed]);

  return (
    <div
      ref={modalContentRef}
      className={classNames(
        styles.wilayaContainer,
        styles.productDropdownContainer,
        styles.width
      )}
    >
      <div className={styles.dropdownContainerr}>
        <div className={styles.customDropdownn}>
          <input
            type="text"
            id="productDropdown"
            className={classNames("dropdown-input", styles.pointedArea)}
            name="product"
            placeholder={t("select_other_request_type")}
            onClick={toggleList}
            readOnly
            value={
              i18n.language === "ar"
                ? type?.arabic_translation
                : i18n.language === "en"
                ? type?.english_translation
                : type?.name
            }
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
          />
          {sources !== null && (
            <div
              className={classNames(styles.productList, {
                [styles.hidden]: !isListDisplayed,
              })}
              id="productList"
            >
              {types.map((commune) => (
                <div
                  key={commune?.name}
                  className={
                    i18n.language === "ar"
                      ? styles.communeListItemArabic
                      : styles.communeListItem
                  }
                  onClick={() => onItemSelected(commune)}
                >
                  {/* {t(commune.name)} */}
                  {i18n.language === "ar"
                    ? commune?.arabic_translation
                    : i18n.language === "en"
                    ? commune?.english_translation
                    : commune?.name}
                </div>
              ))}
            </div>
          )}
          <div
            className={i18n.language === "ar" ? styles.arrowLeft : styles.arrow}
            id="productArrow"
            onClick={toggleList}
          >
            &#9660;
          </div>
        </div>
      </div>
    </div>
  );
};
